'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';

const LK_URL = 'https://lk.team-builder.ru';
const B2B_ROLES = ['owner', 'admin', 'hr'];

export default function Dashboard() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [companies, setCompanies] = useState<any[]>([]);
  const [busy, setBusy] = useState(true);
  const [newName, setNewName] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.myCompanies().then((r: any) => {
      const list = r?.data?.companies ?? [];
      setCompanies(list);

      // Check if user has any B2B role (owner/admin/hr)
      const hasB2BAccess = list.some((c: any) => B2B_ROLES.includes(c.role));

      if (list.length === 0) {
        // No companies at all → show onboarding (create company)
        return;
      }

      if (!hasB2BAccess) {
        // Has companies but only as member → redirect to LK
        window.location.href = LK_URL;
        return;
      }

      // Has B2B role → redirect to first company where they have that role
      const b2bCompany = list.find((c: any) => B2B_ROLES.includes(c.role));
      if (b2bCompany && !showCreate) {
        router.replace(`/companies/${b2bCompany.company.id}`);
      }
    }).catch(() => setCompanies([])).finally(() => setBusy(false));
  }, [user]);

  const createCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    await api.createCompany({ name: newName.trim() });
    const r: any = await api.myCompanies();
    const list = r?.data?.companies ?? [];
    if (list.length > 0) {
      router.push(`/companies/${list[0].company.id}`);
    }
  };

  if (loading || !user) return null;
  if (busy) return <p className="p-6 text-muted-foreground">Загрузка…</p>;

  // No companies OR explicitly creating → show onboarding
  if (companies.length === 0 || showCreate) {
    const isMember = companies.length > 0;
    return (
      <main className="mx-auto max-w-md p-6">
        <div className="rounded-2xl border border-border bg-white p-8 shadow-sm">
          <h1 className="mb-2 text-2xl font-extrabold tracking-tight text-foreground">
            {showCreate ? 'Новая компания' : 'Создайте первую компанию'}
          </h1>
          <p className="mb-5 text-sm text-muted-foreground">
            Создайте компанию, чтобы управлять командами, сотрудниками и наймом.
          </p>
          <form onSubmit={createCompany}>
            <input className="mb-3 w-full rounded-xl border border-input bg-muted/50 px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Название компании"
              value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus />
            <button type="submit"
              className="w-full rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary">
              Создать
            </button>
          </form>
          <div className="mt-4 border-t border-border pt-4">
            <button onClick={() => { window.location.href = LK_URL; }}
              className="w-full text-center text-xs text-muted-foreground hover:text-primary transition">
              Личный кабинет →
            </button>
          </div>
          {showCreate && (
            <button onClick={() => setShowCreate(false)}
              className="mt-3 w-full text-center text-xs text-muted-foreground hover:text-muted-foreground">
              ← Назад
            </button>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-md p-6">
      <p className="text-muted-foreground">Перенаправление…</p>
    </main>
  );
}
